import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTopicDetails, deleteTopic, getTopicDigest, markTopicArticlesRead } from '../services/topics';
import { createDigestHighlight, getDigestHighlightsByTopic } from '../services/highlights';
import { useToast } from '../hooks/useToast';
import { useTextSelection } from '../hooks/useTextSelection';
import { setTitle } from '../utils/environment';
import ShareContextMenu from '../components/ShareContextMenu';
import SharePreviewDialog from '../components/SharePreviewDialog';
import ChatPopup, { ChatFAB } from '../components/ChatPopup';
import useNavigation from '../hooks/useNavigation';
import TopicDigestSection from './TopicDigest';
import TopicArticlesList from './TopicArticlesList';
import { formatDateCompact } from '../utils/dateUtils';

const TopicDetail = () => {
  const { topicId } = useParams();
  const { goBack } = useNavigation();
  const { showSuccess, showError } = useToast();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(() => {
    const savedToggle = sessionStorage.getItem(`topic-${topicId}-showAll`);
    return savedToggle === 'true';
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestExpanded, setDigestExpanded] = useState(true);
  const [markingRead, setMarkingRead] = useState(false);
  const [digestHighlights, setDigestHighlights] = useState([]);
  const [highlightingItem, setHighlightingItem] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [capturedSelection, setCapturedSelection] = useState({
    text: '',
    markdown: '',
    hasMarkdown: false
  });
  const [isCreatingHighlight, setIsCreatingHighlight] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Refs for digest section detection
  const whatsNewRef = useRef(null);
  const coreThemeRef = useRef(null);
  const recommendedReadsRef = useRef(null);

  // Text selection hook
  const {
    selectedText,
    selectedMarkdown,
    hasMarkdown,
    contextMenuVisible,
    contextMenuPosition,
    contextMenuRef,
    hideSelection,
  } = useTextSelection();

  const fetchTopicDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      // Fetch articles with pagination
      const topicData = await getTopicDetails(topicId, {
        unread_only: !showAll,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize
      });
      setTopic(topicData);
      setPagination(topicData.pagination);
      setTitle(`${topicData.label} - Topics`);
    } catch (err) {
      console.error('Failed to fetch topic details:', err);
      setError(err.message);
      setTitle('Topic Not Found');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTopic = async () => {
    if (!window.confirm(`Are you sure you want to delete the topic "${topic?.label}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      await deleteTopic(topicId);
      showSuccess('Topic deleted successfully');
      goBack(); // Go back to previous page
    } catch (err) {
      console.error('Failed to delete topic:', err);
      showError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const articleNavContext = {
    from: 'topic',
    topicId: topicId,
    topicLabel: topic?.label
  };

  const articleNavPageState = {
    showAll: showAll,
    currentPage: currentPage,
    pageSize: pageSize
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerPageChange = (perPage) => {
    setPageSize(perPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const fetchDigest = async () => {
    if (!topicId) return;

    try {
      setDigestLoading(true);
      const digestData = await getTopicDigest(topicId);
      setDigest(digestData);
      setDigestExpanded(true);
      // Fetch existing digest highlights for this topic
      if (topic) {
        await fetchDigestHighlights();
      }
    } catch (err) {
      console.error('Failed to fetch digest:', err);
      showError(err.message);
    } finally {
      setDigestLoading(false);
    }
  };

  const fetchDigestHighlights = async () => {
    if (!topic) return;

    try {
      const response = await getDigestHighlightsByTopic(topic.label);
      setDigestHighlights(response.highlights || []);
    } catch (err) {
      console.error('Failed to fetch digest highlights:', err);
      // Don't show error to user, just log it
    }
  };

  const handleHighlightDigestItem = async (digestText, section, articleId = null) => {
    if (!digest || !topic) return;

    const itemKey = `${section}-${digestText}`;
    // Prevent duplicate requests from rapid clicks or React Strict Mode double-invocation
    if (highlightingItem === itemKey) return;
    setHighlightingItem(itemKey);

    try {
      const highlightData = {
        article_id: articleId, // Link to article if available (enables dual display)
        topic_name: topic.label,
        digest_section: section,
        digest_text: digestText,
        digest_version: digest.generated_at,
      };

      await createDigestHighlight(highlightData);
      showSuccess('Digest item highlighted');

      // Refresh highlights
      await fetchDigestHighlights();
    } catch (err) {
      console.error('Failed to highlight digest item:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setHighlightingItem(null);
    }
  };

  const isDigestItemHighlighted = (digestText) => {
    return digestHighlights.some(h => h.exact_text === digestText);
  };

  // Detect which digest section the selection is in
  const detectDigestSection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const container = range.commonAncestorContainer;
    const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;

    // Check each section ref
    if (whatsNewRef.current?.contains(element)) return 'whats_new';
    if (coreThemeRef.current?.contains(element)) return 'core_theme';
    if (recommendedReadsRef.current?.contains(element)) return 'recommended_reads';

    return null;
  };

  // Handle highlight from context menu (text selection)
  const handleCreateHighlightFromSelection = async () => {
    if (!selectedText || !digest || !topic) {
      hideSelection();
      return;
    }

    const section = detectDigestSection();
    if (!section) {
      showError('Please select text from the digest content');
      hideSelection();
      return;
    }

    try {
      setIsCreatingHighlight(true);

      const highlightData = {
        article_id: null,
        topic_name: topic.label,
        digest_section: section,
        digest_text: selectedText,
        digest_version: digest.generated_at,
      };

      await createDigestHighlight(highlightData);
      showSuccess('Text highlighted');
      await fetchDigestHighlights();
      hideSelection();
    } catch (err) {
      console.error('Failed to create highlight:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setIsCreatingHighlight(false);
    }
  };

  // Share functionality handlers
  const handleShareClick = () => {
    setCapturedSelection({
      text: selectedText,
      markdown: selectedMarkdown,
      hasMarkdown: hasMarkdown
    });
    setShareDialogOpen(true);
    hideSelection();
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setCapturedSelection({
      text: '',
      markdown: '',
      hasMarkdown: false
    });
  };

  const handleMarkAllRead = async () => {
    if (!topicId) return;

    try {
      setMarkingRead(true);
      const result = await markTopicArticlesRead(topicId);
      showSuccess(result.message || `Marked ${result.marked_count} articles as read`);

      // Refresh topic details to update unread counts
      await fetchTopicDetails();
    } catch (err) {
      console.error('Failed to mark articles as read:', err);
      showError(err.message);
    } finally {
      setMarkingRead(false);
    }
  };

  const formatSimilarity = (similarity) => {
    return Math.round(similarity * 100);
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.9) return 'text-fun-mint bg-emerald-50 dark:bg-emerald-950/30';
    if (similarity >= 0.8) return 'text-fun-mint bg-emerald-50 dark:bg-emerald-950/30';
    if (similarity >= 0.7) return 'text-primary-600 bg-primary-50 dark:bg-primary-950/30';
    if (similarity >= 0.6) return 'text-fun-peach bg-amber-50 dark:bg-amber-950/30';
    return 'text-secondary-text bg-surface-hover';
  };

  useEffect(() => {
    if (topicId) {
      fetchTopicDetails();
    }
  }, [topicId, currentPage, pageSize, showAll]);

  // Handle refresh when returning from article navigation
  useEffect(() => {
    if (location.state?.refreshData) {
      fetchTopicDetails();
    }
  }, [location.state?.refreshData]);

  // Automatically fetch digest when topic loads (if there are unread articles)
  useEffect(() => {
    if (topic && topic.unread_count > 0 && !digest && !digestLoading) {
      fetchDigest();
    }
  }, [topic?.id, topic?.unread_count]);

  if (loading) {
    return (
      <div className="min-h-screen bg-clay-bg py-8">
        <div className="max-w-6xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-12">
            <div className="spinner-fun-lg"></div>
            <span className="ml-4 text-lg text-secondary-text">Loading topic details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className="min-h-screen bg-clay-bg py-8">
        <div className="max-w-6xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="card-clay text-center py-12">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="font-display text-2xl font-bold text-heading mb-2">Topic Not Found</h1>
            <p className="text-secondary-text mb-6">{error || 'The topic you\'re looking for doesn\'t exist.'}</p>
            <Link
              to="/topics"
              className="btn-clay inline-flex items-center"
            >
              ← Back to Topics
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // No need for client-side filtering since backend handles it with unread_only parameter
  const filteredArticles = topic.articles;

  return (
    <div className="min-h-screen bg-clay-bg py-8">
      <div className="max-w-6xl mx-auto px-2 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center text-sm text-secondary-text mb-4">
            <Link to="/" className="hover:text-primary-600 transition-colors">Dashboard</Link>
            <span className="mx-2">→</span>
            <Link to="/topics" className="hover:text-primary-600 transition-colors">Topics</Link>
            <span className="mx-2">→</span>
            <span className="text-heading font-medium">{topic.label}</span>
          </div>

          <div className="card-clay p-3 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <h1 className="font-display text-3xl font-bold text-heading mb-2">
                  {topic.label}
                </h1>
                <div className="flex flex-wrap gap-2 sm:gap-4 text-sm text-secondary-text">
                  <span className="badge-fun-accent">📄 {topic.unread_count} unread</span>
                  <span>📚 {topic.article_count} total</span>
                  <span>🗓️ {formatDateCompact(topic.created_at)}</span>
                </div>
              </div>

              <button
                onClick={handleDeleteTopic}
                disabled={deleting}
                className="btn-clay-ghost text-fun-coral hover:bg-rose-50 dark:hover:bg-rose-900/30 disabled:opacity-50 disabled:cursor-not-allowed self-start flex-shrink-0"
              >
                {deleting ? 'Deleting...' : '🗑️ Delete'}
              </button>
            </div>

            {/* Topic Metadata */}
            {topic.topic_metadata && (
              <div className="mt-4 p-4 bg-clay-bg rounded-clay">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-semibold text-body-text">Algorithm:</span>
                    <div className="text-secondary-text">{topic.algorithm}</div>
                  </div>
                  {topic.topic_metadata.silhouette_score && (
                    <div>
                      <span className="font-semibold text-body-text">Quality Score:</span>
                      <div className="text-secondary-text">
                        {(topic.topic_metadata.silhouette_score * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="font-semibold text-body-text">Cluster Size:</span>
                    <div className="text-secondary-text">{topic.topic_metadata.article_count}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-body-text">Generation:</span>
                    <div className="text-secondary-text capitalize">
                      {topic.topic_metadata.generated_from || 'Unknown'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* AI Digest */}
        <TopicDigestSection
          topic={topic}
          digest={digest}
          digestLoading={digestLoading}
          digestExpanded={digestExpanded}
          setDigestExpanded={setDigestExpanded}
          markingRead={markingRead}
          onMarkAllRead={handleMarkAllRead}
          onFetchDigest={fetchDigest}
          onHighlightDigestItem={handleHighlightDigestItem}
          isDigestItemHighlighted={isDigestItemHighlighted}
          highlightingItem={highlightingItem}
          digestHighlights={digestHighlights}
          articleNavContext={articleNavContext}
          articleNavPageState={articleNavPageState}
          whatsNewRef={whatsNewRef}
          coreThemeRef={coreThemeRef}
          recommendedReadsRef={recommendedReadsRef}
        />

        {/* Articles List */}
        <TopicArticlesList
          topicId={topicId}
          articles={filteredArticles}
          showAll={showAll}
          setShowAll={setShowAll}
          pagination={pagination}
          loading={loading}
          onPageChange={handlePageChange}
          onPerPageChange={handlePerPageChange}
          articleNavContext={articleNavContext}
          articleNavPageState={articleNavPageState}
          formatDate={formatDateCompact}
          formatSimilarity={formatSimilarity}
          getSimilarityColor={getSimilarityColor}
        />
      </div>

      {/* Context Menu for text selection in digest */}
      {selectedText && digest && (
        <ShareContextMenu
          visible={contextMenuVisible}
          position={contextMenuPosition}
          onShare={handleShareClick}
          onHighlight={handleCreateHighlightFromSelection}
          onClose={hideSelection}
          hasMarkdown={hasMarkdown}
          showHighlight={true}
          isHighlighting={isCreatingHighlight}
          ref={contextMenuRef}
        />
      )}

      {/* Share Preview Dialog */}
      <SharePreviewDialog
        isOpen={shareDialogOpen}
        onClose={handleCloseShareDialog}
        selectedText={capturedSelection.text}
        selectedMarkdown={capturedSelection.markdown}
        hasMarkdown={capturedSelection.hasMarkdown}
        articleTitle={topic?.label}
        articleUrl={`/topics/${topicId}`}
      />

      {/* Chat FAB and Popup - only show when digest is available */}
      {topic && digest && (
        <>
          <ChatFAB
            onClick={() => setChatOpen(!chatOpen)}
            isOpen={chatOpen}
          />
          <ChatPopup
            isOpen={chatOpen}
            onClose={() => setChatOpen(false)}
            contextType="topic"
            contextId={topicId}
            contextTitle={topic.label || 'Topic'}
          />
        </>
      )}
    </div>
  );
};

export default TopicDetail;